import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    useGetWhatYouLearnBySlugQuery,
    useUpdateWhatYouLearnMutation,
    useDeleteWhatYouLearnMutation,
    useCreateWhatYouLearnMutation,
} from 'api/workshopApi';
import { mediaUrl } from 'utils/mediaUrl';

export const WhatYouLearn = () => {
    const { slug } = useParams();
    const { data, isLoading, isError } = useGetWhatYouLearnBySlugQuery(slug);
    const [updateWatYouLearn] = useUpdateWhatYouLearnMutation();
    const [deleteWhatYouLearn] = useDeleteWhatYouLearnMutation();
    const [createWhatYouLearn] = useCreateWhatYouLearnMutation();

    const [entries, setEntries] = useState([]);
    const [newEntry, setNewEntry] = useState({
        title: '',
        description: '',
        tagline: '',
        logo: null,
    });

    useEffect(() => {
        if (data?.data) {
            setEntries(
                data.data.map(item => ({
                    id: item.id,
                    slug: item.slug,
                    title: item.title,
                    description: item.description,
                    tagline: item.tagline,
                    logo: null,
                    existingLogoUrl: item.logo,
                }))
            );
        }
    }, [data]);

    const handleChange = (id, field, value) => {
        setEntries(prev =>
            prev.map(entry => (entry.id === id ? { ...entry, [field]: value } : entry))
        );
    };

    const handleFileChange = (id, file) => {
        setEntries(prev => prev.map(entry => (entry.id === id ? { ...entry, logo: file } : entry)));
    };

    const handleNewEntryChange = (field, value) => {
        setNewEntry(prev => ({ ...prev, [field]: value }));
    };

    const handleNewFileChange = file => {
        setNewEntry(prev => ({ ...prev, logo: file }));
    };

    const handleCreate = async e => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', newEntry.title);
        formData.append('description', newEntry.description);
        formData.append('tagline', newEntry.tagline);
        if (newEntry.logo) {
            formData.append('logo', newEntry.logo);
        }

        try {
            const res = await createWhatYouLearn({ slug, formData }).unwrap();
            const newData = res.data;
            setEntries(prev => [
                {
                    id: newData.id,
                    slug: newData.slug,
                    title: newData.title,
                    description: newData.description,
                    tagline: newData.tagline,
                    logo: null,
                    existingLogoUrl: newData.logo,
                },
                ...prev,
            ]);
            setNewEntry({ title: '', description: '', tagline: '', logo: null });
            alert('Topic created successfully!');
        } catch (error) {
            console.error('Create failed:', error);
            alert('Failed to create topic.');
        }
    };

    const handleSingleSubmit = async (e, entry) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', entry.title);
        formData.append('description', entry.description);
        formData.append('tagline', entry.tagline);
        if (entry.logo) {
            formData.append('logo', entry.logo);
        }

        try {
            await updateWatYouLearn({
                slug,
                topicSlug: entry.slug,
                body: formData,
            }).unwrap();
            alert(`Updated "${entry.title}" successfully!`);
        } catch (error) {
            console.error('Error updating:', error);
            alert(`Failed to update "${entry.title}".`);
        }
    };

    const handleDelete = async topicSlug => {
        try {
            await deleteWhatYouLearn({ slug, topicSlug }).unwrap();
            setEntries(prev => prev.filter(entry => entry.slug !== topicSlug));
            alert('Deleted successfully!');
        } catch (error) {
            console.error('Delete failed:', error);
            alert('Failed to delete.');
        }
    };

    if (isLoading) return <p className="text-center py-10">Loading...</p>;
    if (isError) return <p className="text-center py-10 text-red-600">Error loading data.</p>;

    return (
        <section className="max-w-4xl mx-auto mt-10 p-6 border rounded-lg shadow bg-white">
            <h2 className="text-2xl font-semibold mb-6 text-center">What You’ll Learn?</h2>

            {/* CREATE FORM */}
            <form
                onSubmit={handleCreate}
                className="border p-4 rounded-lg mb-10 space-y-4 bg-gray-50"
            >
                <h3 className="font-bold text-lg mb-4">Add New Topic</h3>

                <div className="flex flex-col sm:flex-row sm:space-x-4">
                    <div className="flex-1 mb-4 sm:mb-0">
                        <label className="block font-semibold mb-1">Title</label>
                        <input
                            type="text"
                            value={newEntry.title}
                            onChange={e => handleNewEntryChange('title', e.target.value)}
                            className="border p-2 rounded w-full"
                            required
                        />
                    </div>

                    <div className="flex-1">
                        <label className="block font-semibold mb-1">Tagline</label>
                        <input
                            type="text"
                            value={newEntry.tagline}
                            onChange={e => handleNewEntryChange('tagline', e.target.value)}
                            className="border p-2 rounded w-full"
                            required
                        />
                    </div>
                </div>

                <div>
                    <label className="block font-semibold mb-1">Description</label>
                    <textarea
                        value={newEntry.description}
                        onChange={e => handleNewEntryChange('description', e.target.value)}
                        className="border p-2 rounded w-full"
                        rows={4}
                        required
                    />
                </div>

                <div>
                    <label className="block font-semibold mb-1">Logo</label>
                    {newEntry.logo && (
                        <img
                            src={URL.createObjectURL(newEntry.logo)}
                            alt="Logo Preview"
                            className="h-20 mb-2 object-contain"
                        />
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={e => handleNewFileChange(e.target.files[0])}
                        className="w-full"
                    />
                </div>

                <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full sm:w-auto"
                >
                    Create Topic
                </button>
            </form>

            {/* LIST EXISTING TOPICS */}
            {entries.map((entry, idx) => (
                <form
                    key={entry.id}
                    onSubmit={e => handleSingleSubmit(e, entry)}
                    className="border p-4 rounded-lg mb-6 space-y-4"
                >
                    <h3 className="font-bold text-lg mb-4">Topic {idx + 1}</h3>

                    <div className="flex flex-col sm:flex-row sm:space-x-4">
                        <div className="flex-1 mb-4 sm:mb-0">
                            <label className="block font-semibold mb-1">Title</label>
                            <input
                                type="text"
                                value={entry.title}
                                onChange={e => handleChange(entry.id, 'title', e.target.value)}
                                className="border p-2 rounded w-full"
                            />
                        </div>

                        <div className="flex-1">
                            <label className="block font-semibold mb-1">Tagline</label>
                            <input
                                type="text"
                                value={entry.tagline}
                                onChange={e => handleChange(entry.id, 'tagline', e.target.value)}
                                className="border p-2 rounded w-full"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block font-semibold mb-1">Description</label>
                        <textarea
                            value={entry.description}
                            onChange={e => handleChange(entry.id, 'description', e.target.value)}
                            className="border p-2 rounded w-full"
                            rows={4}
                        />
                    </div>

                    <div>
                        <label className="block font-semibold mb-1">Logo</label>
                        {(entry.logo || entry.existingLogoUrl) && (
                            <img
                                src={
                                    entry.logo
                                        ? URL.createObjectURL(entry.logo)
                                        : mediaUrl(entry.existingLogoUrl)
                                }
                                alt="Logo Preview"
                                className="h-20 mb-2 object-contain"
                            />
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={e => handleFileChange(entry.id, e.target.files[0])}
                            className="w-full"
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row sm:space-x-4">
                        <button
                            type="submit"
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded mb-2 sm:mb-0 sm:flex-1"
                        >
                            Update Topic
                        </button>

                        <button
                            type="button"
                            onClick={() => handleDelete(entry.slug)}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded sm:flex-1"
                        >
                            Delete Topic
                        </button>
                    </div>
                </form>
            ))}
        </section>
    );
};

export default WhatYouLearn;
